import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import {
  getMessageDefinition,
  isAppMessageCode,
  type ApiFieldError,
  type ApiMessage,
  type AppMessageCode,
  type MessageParams,
} from '@xtreed/academy-shared';
import i18n from '@/i18n';

const GENERIC_CODES = new Set<AppMessageCode>([
  'INTERNAL_ERROR',
  'BAD_REQUEST',
  'NOT_FOUND',
  'CONFLICT',
  'VALIDATION_FAILED',
  'DATABASE_ERROR',
]);

interface ApiMessagePayload {
  code?: unknown;
  params?: MessageParams;
  errors?: ApiFieldError[];
}

function payloadOf(source: unknown): ApiMessagePayload | null {
  const data = isAxiosError(source) ? source.response?.data : source;
  return data && typeof data === 'object' ? (data as ApiMessagePayload) : null;
}

function toApiMessage(code: AppMessageCode, params?: MessageParams) {
  const { severity } = getMessageDefinition(code);
  return params ? { code, severity, params } : { code, severity };
}

export function getApiMessage(source: unknown): ApiMessage | null {
  const payload = payloadOf(source);
  if (!payload || !isAppMessageCode(payload.code)) return null;

  if (payload.code === 'VALIDATION_FAILED' && payload.errors?.length) {
    const specific =
      payload.errors.find(
        (error) => error.code !== 'VALIDATION_INVALID_FIELD',
      ) ?? payload.errors[0];
    if (isAppMessageCode(specific.code)) {
      return toApiMessage(specific.code, specific.params);
    }
  }

  return toApiMessage(payload.code, payload.params);
}

export function translateApiMessage(message: ApiMessage): string {
  return i18n.t(`errors:${message.code}`, message.params ?? {});
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const message = getApiMessage(error);
  if (!message || GENERIC_CODES.has(message.code)) return fallback;
  return translateApiMessage(message);
}

export function notifyApiError(error: unknown, fallback: string) {
  const message = getApiMessage(error);
  if (!message || GENERIC_CODES.has(message.code)) {
    toast.error(fallback);
    return;
  }
  toast[message.severity](translateApiMessage(message));
}

export function notifyApiMessage(source: unknown, fallback?: string) {
  const message = getApiMessage(source);
  if (message) {
    toast[message.severity](translateApiMessage(message));
  } else if (fallback) {
    toast.success(fallback);
  }
}
